/* global Razorpay */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from 'sonner';
import { CreditCard, Calendar, IndianRupee, ArrowRight, Loader2 } from 'lucide-react';
import UsageDetails from "./UsageDetails";
import { useTheme } from '@mui/material/styles';

const Billing = () => {
  const [currentPlan, setCurrentPlan] = useState("Premium");
  const [nextBillingDate, setNextBillingDate] = useState("May 19, 2025");
  const [amountDue, setAmountDue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [user, setUser] = useState({});
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    const fetchUserAndBillingDetails = async () => {
      try {
        setIsLoading(true);
        const userId = localStorage.getItem('userId');
        if (!userId) {
          throw new Error('User ID not found');
        }

        // Fetch user details
        const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        if (!userResponse.ok) {
          throw new Error('Failed to fetch user details');
        }
        const userData = await userResponse.json();
        setUser(userData);

        // Fetch billing details
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/plan?userId=${userId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch billing details');
        }
        const data = await response.json();
        setCurrentPlan(data.planDetails.name);
        setAmountDue(data.planDetails.price);

        if (data.planDetails.lastPaymentDate) {
          const lastPaymentDate = new Date(data.planDetails.lastPaymentDate);
          const nextBillingDate = new Date(lastPaymentDate);
          nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
          setNextBillingDate(nextBillingDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
          setAmountDue(0);
        } else {
          const nextBillingDate = new Date();
          nextBillingDate.setDate(nextBillingDate.getDate() + 30);
          setNextBillingDate(nextBillingDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch (error) {
        console.error('Error fetching billing details:', error);
        toast.error('Failed to load billing details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserAndBillingDetails();
  }, []);

  const updateBillingInfo = () => {
    // This function will be called after successful payment
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setNextBillingDate(nextMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    setAmountDue(0);
  };

  const paymentHandler = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const gstRate = 0.18; // 18% GST
      const amountWithoutGST = amountDue;
      const gstAmount = Math.round(amountWithoutGST * gstRate);
      const totalAmount = amountWithoutGST + gstAmount;
      const amountInPaise = totalAmount * 100; // Convert to paise
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/payment/order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            amount: amountInPaise, 
            userId,
            amountWithoutGST,
            gstAmount,
            totalAmount
          }), 
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create order");
      }

      const { data: order } = await response.json();

      const razorpayKey ='rzp_test_hoKCmKm0sSq6xR';
      const options = {
        key: razorpayKey,
        amount: order.amount, // Amount in paise
        currency: "INR",
        name: "verbalyze",
        description: `Payment for ${currentPlan} subscription (Incl. GST)`,
        order_id: order.id, 
        handler: function (response) {
          // Send payment details to backend for verification
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              userId,
              amountWithoutGST,
              gstAmount,
              totalAmount
            }),
          })
            .then((res) => res.json())
            .then((verificationResponse) => {
              if (verificationResponse.success) {
                toast.success(`Payment of ₹${totalAmount} (including ₹${gstAmount} GST) Verified!`);
                updateBillingInfo(); // Update billing information after successful payment
              } else {
                toast.error("Payment Verification Failed!");
              }
            })
            .catch((error) => {
              console.error("Payment verification failed:", error);
              toast.error("Something went wrong while verifying payment!");
            });
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`.trim(),
          email: user.email || "",
          contact: user.phone || "",
        },
        theme: {
          color: "#4CAF50",
        },
      };

      // Open Razorpay checkout
      const rzp1 = new Razorpay(options);
      rzp1.open();
      rzp1.on("payment.failed", function (response) {
        console.error("Payment failed:", response);
        toast.error("Payment failed! Please try again.");
      });
      
    } catch (error) {
      console.error("Payment initiation failed:", error);
      toast.error("Something went wrong while initiating payment!");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className={`w-full max-w-7xl mx-auto ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
      <Toaster position="top-right" className="mt-24" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <div className="flex items-center space-x-2">
          <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>Billing Overview</h2>
          <div className={`h-1 w-16 bg-gradient-to-r ${isDarkMode ? 'from-blue-600 to-blue-400' : 'from-blue-400 to-blue-600'} rounded-full`}></div>
        </div>
        
        <motion.div 
          className={`${isDarkMode ? 'bg-slate-800 border-blue-900' : 'bg-white border-blue-100'} rounded-2xl shadow-lg overflow-hidden border`}
          whileHover={{ y: -4 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Loader2 className={`h-8 w-8 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                </motion.div>
                <p className={`mt-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Loading billing details...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div 
                    className="flex items-start space-x-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <CreditCard className={`h-6 w-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Current Plan</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>{currentPlan}</p>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <Calendar className={`h-6 w-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Next Billing Date</p>
                      <p className={`text-lg font-medium ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>{nextBillingDate}</p>
                    </div>
                  </motion.div>
                </div>
                
                <div className={`h-px w-full bg-gradient-to-r from-transparent ${isDarkMode ? 'via-blue-800' : 'via-blue-200'} to-transparent`}></div>
                
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <motion.div 
                    className="flex items-start space-x-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <IndianRupee className={`h-6 w-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Amount Due</p>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>{formatCurrency(amountDue)}  + GST</p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>GST (18%): {formatCurrency(Math.round(amountDue * 0.18))}</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                        Total: {formatCurrency(amountDue + Math.round(amountDue * 0.18))}
                      </p>
                    </div>
                  </motion.div>
                  
                  <motion.button
                    onClick={amountDue > 0 ? paymentHandler : undefined}
                    disabled={isPaymentProcessing || amountDue === 0}
                    className={`relative overflow-hidden rounded-full px-6 py-3 font-medium text-white shadow-lg transition-all duration-300 
                      ${isPaymentProcessing || amountDue === 0
                        ? `${isDarkMode ? 'bg-orange-700' : 'bg-orange-300'} cursor-not-allowed opacity-70` 
                        : `bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white font-medium text-lg shadow-md flex items-center justify-center`
                      }`}
                    whileHover={{ 
                      scale: amountDue > 0 ? 1.05 : 1, 
                      boxShadow: amountDue > 0 ? "0 0 15px rgba(255, 165, 0, 0.5)" : "none",
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: amountDue > 0 ? 0.95 : 1 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <AnimatePresence mode="wait">
                      {isPaymentProcessing ? (
                        <motion.div
                          key="processing"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center space-x-2"
                        >
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <Loader2 className="h-4 w-4" />
                          </motion.div>
                          <span>Processing...</span>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="pay"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center space-x-2"
                        >
                          {amountDue > 0 ? (
                            <>
                              <span>Pay Now</span>
                              <ArrowRight className="h-4 w-4" />
                            </>
                          ) : (
                            <span>No Payment Due</span>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <UsageDetails />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Billing;
